const BERLIN_TIME_ZONE = "Europe/Berlin";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Returns an accessor that builds its Berlin formatter on first use.
 *
 * `new Intl.DateTimeFormat(...)` throws when the runtime cannot resolve
 * Europe/Berlin. Constructing these at module load would make that an
 * import-time crash, before any caller's try/catch exists. Deferring it means
 * the failure surfaces at call time, where {@link getBerlinTodayDateOnlyOrEarlier}
 * and friends can handle it.
 *
 * The locale only fixes the parts format for `formatToParts`; it is not a
 * display locale (see {@link formatBerlinDateOnly} for that).
 */
const createLazyBerlinFormatter = (options: Intl.DateTimeFormatOptions) => {
  let formatter: Intl.DateTimeFormat | undefined;

  return (): Intl.DateTimeFormat => {
    // A failed construction is not cached, so a later call retries.
    formatter ??= new Intl.DateTimeFormat("en-US", {
      timeZone: BERLIN_TIME_ZONE,
      ...options,
    });
    return formatter;
  };
};

const getBerlinDatePartsFormatter = createLazyBerlinFormatter({
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const getBerlinTimePartsFormatter = createLazyBerlinFormatter({
  hourCycle: "h23",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

type DateOnlyParts = {
  year: number;
  monthIndex: number;
  day: number;
};

const parseDateOnlyParts = (value: string): DateOnlyParts | null => {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) {
    return null;
  }

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const monthIndex = Number(monthValue) - 1;
  const day = Number(dayValue);
  const validationDate = new Date(Date.UTC(year, monthIndex, day, 12));

  if (
    validationDate.getUTCFullYear() !== year ||
    validationDate.getUTCMonth() !== monthIndex ||
    validationDate.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, monthIndex, day };
};

const formatUtcDateOnly = (date: Date): string =>
  [
    String(date.getUTCFullYear()).padStart(4, "0"),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");

const toUtcNoon = (value: string): Date | null => {
  const parts = parseDateOnlyParts(value);
  if (!parts) {
    return null;
  }

  return new Date(Date.UTC(parts.year, parts.monthIndex, parts.day, 12));
};

export const isValidDateOnly = (value: string): boolean =>
  parseDateOnlyParts(value) !== null;

export const compareDateOnly = (left: string, right: string): number | null => {
  if (!isValidDateOnly(left) || !isValidDateOnly(right)) {
    return null;
  }

  return left.localeCompare(right);
};

export const getBerlinTodayDateOnly = (now = new Date()): string => {
  const parts = getBerlinDatePartsFormatter().formatToParts(now);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) {
    throw new Error("Unable to determine the current date in Europe/Berlin");
  }

  return `${year}-${month}-${day}`;
};

/**
 * Same as {@link getBerlinTodayDateOnly}, but falls back to the UTC calendar
 * date when `Intl` cannot resolve Europe/Berlin instead of throwing. Berlin is
 * UTC+1/+2, so the UTC date is either today in Berlin or the day before —
 * never later. That makes this safe for callers using the value as an
 * inclusive lower bound: a broken `Intl` widens the range by at most one day
 * rather than dropping still-relevant entries, and never takes down a render.
 */
export const getBerlinTodayDateOnlyOrEarlier = (now = new Date()): string => {
  try {
    return getBerlinTodayDateOnly(now);
  } catch (error) {
    console.error(
      "Falling back to the UTC calendar date for Europe/Berlin",
      error,
    );
    return formatUtcDateOnly(now);
  }
};

export const getBerlinTimeOfDayInMilliseconds = (now: Date): number => {
  const parts = getBerlinTimePartsFormatter().formatToParts(now);
  const hour = Number(parts.find((part) => part.type === "hour")?.value);
  const minute = Number(parts.find((part) => part.type === "minute")?.value);
  const second = Number(parts.find((part) => part.type === "second")?.value);

  if (![hour, minute, second].every(Number.isFinite)) {
    throw new Error("Unable to determine the current time in Europe/Berlin");
  }

  return ((hour * 60 + minute) * 60 + second) * 1000 + now.getUTCMilliseconds();
};

export const addDaysToDateOnly = (value: string, amount: number): string => {
  const date = toUtcNoon(value);
  if (!date) {
    throw new Error(`Invalid date-only value: ${value}`);
  }

  date.setUTCDate(date.getUTCDate() + amount);
  return formatUtcDateOnly(date);
};

export const getBerlinTomorrowDateOnly = (now = new Date()): string =>
  addDaysToDateOnly(getBerlinTodayDateOnly(now), 1);

export const dateOnlyToLocalDate = (value: string): Date | null => {
  const parts = parseDateOnlyParts(value);
  if (!parts) {
    return null;
  }

  return new Date(parts.year, parts.monthIndex, parts.day);
};

export const formatBerlinDateOnly = (
  value: string,
  locale: string,
): string | null => {
  const date = toUtcNoon(value);
  if (!date) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: BERLIN_TIME_ZONE,
    }).format(date);
  } catch (error) {
    // Same reasoning as the lazy formatters above: callers already fall back
    // to the raw date-only string, which beats blanking the screen.
    console.error("Unable to format a date in Europe/Berlin", error);
    return null;
  }
};
